import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../api/adminApi';
import { PageHeader, FilterPills, Pagination, Avatar, PlanBadge, cardStyle, tableHeaderStyle, tableCellStyle, LoadingState, ErrorState, timeAgo } from './shared';

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<api.AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const planMap: Record<string, string | undefined> = { all: undefined, pro: 'pro', enterprise: 'enterprise', free: 'free' };
      const result = await api.getUsers({ search: search || undefined, plan: planMap[planFilter], page, limit: 7 });
      setUsers(result.users); setTotal(result.total); setTotalPages(result.totalPages); setPage(result.page);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, planFilter, search]);

  if (error && !users.length) return <ErrorState message={error} onRetry={load} />;

  return (
    <div>
      <PageHeader title="Users" subtitle={`${total.toLocaleString()} total users · ${users.filter(u => u.isActive).length} active today`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{
                padding: '8px 12px 8px 36px', fontSize: 13, borderRadius: 8, border: '1px solid #E8E0D8',
                backgroundColor: '#fff', width: 220, fontFamily: '"Geist", system-ui, sans-serif', outline: 'none',
              }}
            />
            <svg style={{ position: 'absolute', left: 10, top: 9, color: '#8B7D6B' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
          <button style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid #E8E0D8', backgroundColor: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: '"Geist", system-ui, sans-serif' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            Filters
          </button>
          <button style={{ padding: '8px 16px', borderRadius: 8, border: 'none', backgroundColor: '#1A1714', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: '"Geist", system-ui, sans-serif' }}>
            Export CSV
          </button>
        </div>
      </PageHeader>

      <FilterPills
        options={[
          { label: 'All users', value: 'all' },
          { label: 'Pro', value: 'pro' },
          { label: 'Enterprise', value: 'enterprise' },
          { label: 'Free', value: 'free' },
          { label: 'Inactive', value: 'inactive', color: '#D46A3A' },
          { label: 'Limit hit', value: 'limit_hit', color: '#D46A3A' },
        ]}
        value={planFilter}
        onChange={(v) => { setPlanFilter(v); setPage(1); }}
      />

      <div style={cardStyle}>
        {loading && !users.length ? <LoadingState /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>User</th>
                <th style={tableHeaderStyle}>Plan</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={tableHeaderStyle}>Usage this month</th>
                <th style={tableHeaderStyle}>Last login</th>
                <th style={tableHeaderStyle}>Joined</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/users/${u._id}`)}>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={u.displayName} size={32} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{u.displayName}</div>
                        <div style={{ fontSize: 12, color: '#8B7D6B' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}><PlanBadge plan={u.plan} pastDue={u.pastDue} /></td>
                  <td style={tableCellStyle}>
                    <span style={{ color: u.isActive ? '#22883F' : '#D44A3A', fontSize: 18 }}>●</span>
                  </td>
                  <td style={{ ...tableCellStyle, fontSize: 13, fontFamily: '"JetBrains Mono", monospace', color: u.limitHit ? '#D44A3A' : '#8B7D6B' }}>
                    {u.limitHit
                      ? `${u.conversations}/7 conv · ${u.fileOps ?? 0}/20`
                      : `${u.conversations} conv · ${u.fileOps ?? 0} files`}
                  </td>
                  <td style={{ ...tableCellStyle, fontSize: 13, color: '#8B7D6B' }}>
                    {u.lastLoginAt ? timeAgo(u.lastLoginAt) : '—'}
                  </td>
                  <td style={{ ...tableCellStyle, fontSize: 13 }}>
                    {new Date(u.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                    <span style={{ color: '#D46A3A', fontSize: 13, fontWeight: 500, marginRight: 12, cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${u._id}`); }}>View</span>
                    <span style={{ color: '#D46A3A', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${u._id}`); }}>Edit</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <Pagination page={page} totalPages={totalPages} total={total} label="users" onPageChange={setPage} />
      </div>
    </div>
  );
}
